import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { useTenant } from '../../../contexts/TenantContext';
import { usePayoutsList } from '../../../hooks/usePayoutsList';
import Sidebar from '../../admin-dashboard/components/Sidebar';
import PaymentRowCard from '../../../components/payments-v2/PaymentRowCard';
import CommissionBreakdownPanel from '../../../components/payments-v2/CommissionBreakdownPanel';

/**
 * Admin V2 Manager Payments — Refonte cards-mobile-first.
 *
 * Route : /admin/manager-payments (V2Switcher) + /admin/v2/manager-payments.
 * Feature : pages.managers.payments_admin.
 *
 * R2 : amounts cryptés AES-256 — affichage "—" + badge AES tant que l'Edge
 *      Function `generic-decrypt-payouts` (Sprint 5) n'est pas déployée.
 * R3 : mobile-first cards + drill-down expand on click.
 */
const MONTHS = [
  'jan', 'feb', 'mar', 'apr', 'may', 'jun',
  'jul', 'aug', 'sep', 'oct', 'nov', 'dec',
];

const AdminV2ManagerPayments = () => {
  const { t } = useTranslation();
  const { userProfile } = useAuth();
  const { tenantName } = useTenant();
  const [collapsed, setCollapsed] = useState(false);
  const [expandedKey, setExpandedKey] = useState(null);

  const { rows, decryptionAvailable, loading, error, reload } = usePayoutsList({ agentId: null });

  // Group by year for visual sections
  const byYear = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      const list = map.get(r.period_year) || [];
      list.push(r);
      map.set(r.period_year, list);
    }
    return Array.from(map.entries()).sort((a, b) => b[0] - a[0]);
  }, [rows]);

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      <Sidebar collapsed={collapsed} onToggle={() => setCollapsed((c) => !c)} userEmail={userProfile?.email} />

      <main className="flex-1 min-w-0 overflow-x-hidden">
        <header className="border-b border-border bg-card sticky top-0 z-10">
          <div className="px-4 md:px-6 py-4">
            <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground">
              {t('paymentsV2.adminTitle', 'Manager payments')}
            </h1>
            <p className="text-xs text-muted-foreground mt-0.5">
              {tenantName} · {rows.length} {t('paymentsV2.periods', 'periods')}
            </p>
          </div>
        </header>

        <div className="p-4 md:p-6 space-y-4 md:space-y-6">
          {error && (
            <div className="rounded-2xl bg-error/5 border border-error/30 p-4 text-sm text-error flex items-center justify-between">
              <span>{String(error.message || error)}</span>
              <button type="button" onClick={reload} className="px-3 py-1.5 rounded-lg border border-error/40 hover:bg-error/10 text-xs font-medium">
                {t('common.retry', 'Retry')}
              </button>
            </div>
          )}

          {!decryptionAvailable && (
            <div className="rounded-2xl bg-accent/5 border border-accent/20 p-4 text-xs text-muted-foreground">
              <span className="font-semibold text-accent mr-1">{t('paymentsV2.aesNoteLabel', 'AES heads-up:')}</span>
              {t(
                'paymentsV2.aesNoteEdgeMissing',
                'Decryption Edge Function is not reachable. Amounts stay hidden until the function is deployed and ENCRYPTION_KEY_GENERIC is set.'
              )}
            </div>
          )}

          {loading && rows.length === 0 ? (
            <div className="space-y-3">
              <div className="h-20 skeleton rounded-2xl" />
              <div className="h-20 skeleton rounded-2xl" />
            </div>
          ) : rows.length === 0 ? (
            <div className="rounded-2xl bg-card border border-border p-8 text-center text-sm text-muted-foreground">
              {t('paymentsV2.empty', 'No manager payouts recorded yet.')}
            </div>
          ) : (
            byYear.map(([year, list]) => (
              <section key={year} className="space-y-2">
                <div className="text-xs uppercase tracking-widest text-muted-foreground/70 font-semibold px-1">
                  {year}
                </div>
                <div className="space-y-2">
                  {list.map((g) => {
                    const monthLabel = t(`months.${g.period_month - 1}`, MONTHS[g.period_month - 1]);
                    const title = `${monthLabel} ${g.period_year}`;
                    const subtitle = g.agent_name
                      ? `${t('paymentsV2.manager', 'Manager')} — ${g.agent_name}`
                      : t('paymentsV2.unassigned', 'No manager assigned');
                    const secondary = `${g.creator_count} ${t('paymentsV2.creators', 'creators')}`;
                    const isExpanded = expandedKey === g.key;
                    return (
                      <PaymentRowCard
                        key={g.key}
                        title={title}
                        subtitle={subtitle}
                        amount={g.totalAmount}
                        secondary={secondary}
                        status={g.status}
                        expanded={isExpanded}
                        onClick={() => setExpandedKey(isExpanded ? null : g.key)}
                      >
                        <CommissionBreakdownPanel
                          breakdown={g.creators.map((c) => ({
                            creator_id: c.creator_id,
                            creator_name: c.creator_name || (c.creator_id ? `${t('paymentsV2.creator', 'Creator')} ${c.creator_id.slice(0, 8)}` : '—'),
                            diamonds: c.diamonds,
                            amount: c.amount,
                            commission: c.commission,
                            status: c.status,
                          }))}
                          creatorBasePath="/admin/v2/creators"
                          summary={{ totalAmount: g.totalAmount, totalCommission: g.totalCommission, count: g.creator_count }}
                          loading={false}
                        />
                      </PaymentRowCard>
                    );
                  })}
                </div>
              </section>
            ))
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminV2ManagerPayments;
